import cn from 'classnames';
import {
  type MouseEvent as ReactMouseEvent,
  ReactNode,
  RefObject,
  useCallback,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
} from 'react';
import { createPortal } from 'react-dom';
import styles from './Popover.module.css';

export type PopoverPlacement = 'bottom' | 'top';
export type PopoverAlign = 'start' | 'end';
export type PopoverTrigger = 'click' | 'hover';

export interface PopoverProps {
  /** The element that toggles the popover on click. */
  children: ReactNode;
  /** The popover body, rendered in a portal on `document.body`. */
  content: ReactNode;
  /** Whether the popover opens below (default) or above the trigger. */
  placement?: PopoverPlacement;
  /** Align the popover's left edge to the trigger's left (`start`, default) or right edge to the trigger's right (`end`). */
  align?: PopoverAlign;
  /** Gap in px between the trigger and the popover. */
  offset?: number;
  /**
   * Show a triangle/arrow on the popover border pointing back at the trigger.
   */
  showArrow?: boolean;
  /**
   * Element the arrow should point at (e.g. a trailing icon inside the
   * trigger). When set, the arrow tracks this element's horizontal center; when
   * omitted it falls back to the trigger's right edge. Re-measured on every open
   * so it stays correct after column resizes.
   */
  arrowTargetRef?: RefObject<HTMLElement | null>;
  className?: string;
  popoverClassName?: string;
  disabled?: boolean;
  /**
   * How the popover opens. `click` (default) toggles and closes on an outside
   * click; `hover` opens on pointer-enter and closes once the pointer has left
   * both the trigger and the popover, so the content stays reachable.
   */
  trigger?: PopoverTrigger;
}

/** Half the arrow's visual width, plus its border — used to keep it clear of the popover's rounded corners. */
const ARROW_HALF = 8;
/** Extra gap (px) so the arrow never touches the popover's rounded corners. */
const ARROW_CORNER_GAP = 6;

/**
 * A click-triggered popover that renders its content in a portal on
 * `document.body` with fixed positioning anchored to the trigger. Unlike the
 * CSS-absolute `Tooltip`, this escapes ancestors that clip overflow (e.g. table
 * cells with `overflow: hidden`), so it stays fully visible. Toggles on click,
 * closes on outside click.
 */
export const Popover = ({
  children,
  content,
  placement = 'bottom',
  align = 'start',
  offset = 4,
  showArrow = false,
  arrowTargetRef,
  className,
  popoverClassName,
  disabled = false,
  trigger = 'click',
}: PopoverProps) => {
  const [isOpen, setIsOpen] = useState(false);
  const [coords, setCoords] = useState({ top: 0, left: 0 });
  // Which vertical side the popover actually resolved to (may differ from
  // `placement` after the flip), and the arrow's horizontal offset (px) from
  // the popover's left edge so it points back at the trigger's icon.
  const [side, setSide] = useState<PopoverPlacement>(placement);
  const [arrowLeft, setArrowLeft] = useState(0);
  const anchorRef = useRef<HTMLDivElement>(null);
  const popoverRef = useRef<HTMLDivElement>(null);

  const updatePosition = useCallback(() => {
    const anchor = anchorRef.current;
    if (!anchor) return;
    const rect = anchor.getBoundingClientRect();
    const popoverHeight = popoverRef.current?.offsetHeight ?? 0;
    const popoverWidth = popoverRef.current?.offsetWidth ?? 0;
    const viewportHeight = window.innerHeight;
    const viewportWidth = window.innerWidth;
    const margin = 8;

    // The screen-x the arrow should point at: a specific target when given
    // (e.g. a trailing icon), else the trigger's right edge. Re-measured on each
    // open so it stays correct after the column (and thus the icon) is resized.
    // A hover popover describes its whole trigger rather than one icon in it,
    // so with no explicit target it centres on the trigger instead.
    const targetEl = arrowTargetRef?.current;
    const targetRect = targetEl?.getBoundingClientRect();
    const centresOnTrigger = !targetRect && trigger === 'hover';
    const targetCenterX = targetRect
      ? targetRect.left + targetRect.width / 2
      : centresOnTrigger
        ? rect.left + rect.width / 2
        : rect.right - ARROW_HALF;

    // Vertical: preferred side, flipped above on bottom overflow, pinned to the
    // top margin if that overflows too.
    let top =
      placement === 'top'
        ? rect.top - popoverHeight - offset
        : rect.bottom + offset;
    let resolvedSide: PopoverPlacement = placement;
    if (top + popoverHeight > viewportHeight) {
      top = rect.top - popoverHeight - offset;
      resolvedSide = 'top';
    }
    if (top < margin) top = margin;

    // Horizontal: when there's an arrow, anchor the popover to the arrow target
    // (icon) instead of the trigger edge — position it so the target sits a
    // fixed inset from the popover's RIGHT edge, i.e. the popover "grows left
    // from the icon". This keeps the icon within the popover's width even when
    // the name+icon row is wider than the popover, so the arrow can reach it.
    // Falls back to the `align` behavior when there's no arrow target.
    // Centring on the trigger instead puts the popover's middle on the target,
    // so the arrow lands in the centre rather than near the right edge.
    const arrowInsetFromRight = ARROW_HALF * 2 + ARROW_CORNER_GAP;
    let left = centresOnTrigger
      ? targetCenterX - popoverWidth / 2
      : showArrow
        ? targetCenterX - popoverWidth + arrowInsetFromRight
        : align === 'end'
          ? rect.right - popoverWidth
          : rect.left;

    // Clamp to the viewport so it never opens off-screen (unreachable, since we
    // close on scroll).
    if (left + popoverWidth > viewportWidth) {
      left = viewportWidth - popoverWidth - margin;
    }
    if (left < margin) left = margin;

    setCoords({ top, left });
    setSide(resolvedSide);

    // Arrow's left within the popover = target screen-x minus the popover's left
    // edge, minus ARROW_HALF to center the triangle. Derived from the final
    // `left`, so it stays on the icon even if the viewport clamp shifted the
    // popover. Clamped to stay off the rounded corners (only hit in the rare
    // case the clamp moved the popover away from the icon).
    if (showArrow) {
      const clampedArrowLeft = Math.max(
        ARROW_HALF + ARROW_CORNER_GAP,
        Math.min(
          targetCenterX - left - ARROW_HALF,
          popoverWidth - ARROW_HALF * 3 - ARROW_CORNER_GAP,
        ),
      );
      setArrowLeft(clampedArrowLeft);
    }
  }, [align, offset, placement, showArrow, arrowTargetRef, trigger]);

  // Position once when opened. We intentionally close, rather than reposition,
  // on scroll
  useLayoutEffect(() => {
    if (!isOpen) return;
    updatePosition();
    const close = () => setIsOpen(false);
    window.addEventListener('scroll', close, true);
    window.addEventListener('resize', close);
    return () => {
      window.removeEventListener('scroll', close, true);
      window.removeEventListener('resize', close);
    };
  }, [isOpen, updatePosition]);

  // Close on outside click. Hover mode closes on pointer-leave instead.
  useEffect(() => {
    if (!isOpen || trigger === 'hover') return;
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as Node;
      if (
        !anchorRef.current?.contains(target) &&
        !popoverRef.current?.contains(target)
      ) {
        setIsOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [isOpen, trigger]);

  // Hover mode: close once the pointer has left BOTH the trigger and the
  // popover. The popover is portalled to document.body, so a plain
  // onMouseLeave on the trigger would close it the moment the pointer crossed
  // the gap towards the content, making the content unreachable.
  const closeIfPointerAway = useCallback((event: ReactMouseEvent) => {
    const next = event.relatedTarget;
    // relatedTarget is null when leaving the window, and is not a Node when
    // leaving towards an SVG-internal element in some browsers.
    if (next instanceof Node) {
      if (
        anchorRef.current?.contains(next) ||
        popoverRef.current?.contains(next)
      ) {
        return;
      }
    }
    setIsOpen(false);
  }, []);

  const hoverHandlers =
    trigger === 'hover'
      ? {
          onMouseEnter: () => setIsOpen(true),
          onMouseLeave: closeIfPointerAway,
        }
      : {};

  if (disabled) return <>{children}</>;

  return (
    <div
      ref={anchorRef}
      className={cn(styles.trigger, className)}
      {...hoverHandlers}
    >
      {trigger === 'hover' ? (
        // Hover popovers describe their trigger rather than being opened by
        // it, so the trigger stays plain content: no button role, and it is
        // reached by keyboard through whatever it wraps.
        children
      ) : (
        <div
          role='button'
          tabIndex={0}
          aria-expanded={isOpen}
          onClick={() => setIsOpen(prev => !prev)}
          onKeyDown={e => {
            if (e.key === 'Enter' || e.key === ' ') {
              e.preventDefault();
              setIsOpen(prev => !prev);
            }
          }}
        >
          {children}
        </div>
      )}
      {isOpen &&
        content &&
        createPortal(
          <div
            ref={popoverRef}
            className={cn(styles.popover, popoverClassName)}
            style={{ top: `${coords.top}px`, left: `${coords.left}px` }}
            role={trigger === 'hover' ? 'tooltip' : 'dialog'}
            // Stop React's synthetic bubbling into this wrapper (portals bubble
            // through the React tree, not the DOM tree).
            onClick={e => e.stopPropagation()}
            {...hoverHandlers}
          >
            {showArrow && (
              <span
                className={cn(
                  styles.arrow,
                  side === 'top' ? styles.arrowDown : styles.arrowUp,
                )}
                style={{ left: `${arrowLeft}px` }}
              />
            )}
            {content}
          </div>,
          document.body,
        )}
    </div>
  );
};

export default Popover;
